/* KBW-82 — Professional buyer program: the application form.

   The form used to be Shopify's own `contact` form and needed no script at
   all. It now posts, as multipart/form-data, straight to the app's proxy
   endpoint (see kbw82-contract.md), so this file is what makes that happen:
   it intercepts the submit, builds the body from the form itself, sends it
   with fetch, and shows whatever the app answers with. No page reload.

   Also drives the one file field this form can have (the certificate) and
   resets the Cloudflare Turnstile widget after a failed attempt, since a
   stale token is refused on the next try.

   No framework. This theme is moving away from Alpine, and a plain form
   with a handful of listeners does not need it.

   Markup contract:
     [data-kbw-bp-form]           the form itself; data-endpoint holds the
                                   URL to post to
     [data-kbw-bp-ok]             success message, hidden until a 200 comes
                                   back
     [data-kbw-bp-err]            error box, hidden until something goes
                                   wrong
     [data-kbw-bp-err-text]       where the server's (or the network's)
                                   error sentence is written
     [data-kbw-bp-submit]         the submit button; data-label holds the
                                   text to restore once the request settles
     [data-kbw-bp-turnstile]      the Cloudflare widget's own container,
                                   reset after a failed submission

     [data-kbw-bp-file]           wrapper for one file field
     [data-kbw-bp-file-input]     the (visually hidden) <input type="file">
     [data-kbw-bp-file-trigger]   button that opens the file picker
     [data-kbw-bp-file-name]      the "chosen file" row, hidden until a file
                                   is picked
     [data-kbw-bp-file-name-text] where the chosen file's name is written
     [data-kbw-bp-file-remove]    clears the chosen file
     [data-kbw-bp-file-error]     the field's own error line (type or size),
                                   hidden until there is something to say

   With no script: the file field still works as a native file input (no
   trigger button, no filename row, but a file can still be chosen), and the
   form falls back to its own action/method — a real POST to the same
   endpoint, just with the raw JSON answer shown instead of this page's own
   success or error state.
*/

(function () {
  'use strict';

  var MAX_FILE_BYTES = 5 * 1024 * 1024;
  var ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  var ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

  var NETWORK_ERROR_MESSAGE = 'We could not reach the server. Please check your connection and try again.';
  var GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
  var FILE_TOO_LARGE_MESSAGE = 'This file is larger than 5 MB. Please choose a smaller file.';
  var FILE_WRONG_TYPE_MESSAGE = 'Please choose a PDF, JPG or PNG file.';

  function fileTypeIsAllowed(file) {
    if (file.type && ALLOWED_TYPES.indexOf(file.type) !== -1) {
      return true;
    }
    var name = (file.name || '').toLowerCase();
    for (var i = 0; i < ALLOWED_EXTENSIONS.length; i++) {
      var ext = ALLOWED_EXTENSIONS[i];
      if (name.slice(name.length - ext.length) === ext) {
        return true;
      }
    }
    return false;
  }

  function showFileError(wrap, message) {
    var errorEl = wrap.querySelector('[data-kbw-bp-file-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function hideFileError(wrap) {
    var errorEl = wrap.querySelector('[data-kbw-bp-file-error]');
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  function showFileName(wrap, name) {
    var nameRow = wrap.querySelector('[data-kbw-bp-file-name]');
    var nameText = wrap.querySelector('[data-kbw-bp-file-name-text]');
    var trigger = wrap.querySelector('[data-kbw-bp-file-trigger]');
    if (nameText) nameText.textContent = name;
    if (nameRow) nameRow.hidden = false;
    if (trigger) trigger.textContent = 'Change file';
  }

  function hideFileName(wrap) {
    var nameRow = wrap.querySelector('[data-kbw-bp-file-name]');
    var nameText = wrap.querySelector('[data-kbw-bp-file-name-text]');
    var trigger = wrap.querySelector('[data-kbw-bp-file-trigger]');
    if (nameText) nameText.textContent = '';
    if (nameRow) nameRow.hidden = true;
    if (trigger) trigger.textContent = 'Choose file';
  }

  function clearFile(wrap) {
    var input = wrap.querySelector('[data-kbw-bp-file-input]');
    if (input) input.value = '';
    hideFileName(wrap);
    hideFileError(wrap);
  }

  function setUpFileField(wrap) {
    var input = wrap.querySelector('[data-kbw-bp-file-input]');
    var trigger = wrap.querySelector('[data-kbw-bp-file-trigger]');
    var removeButton = wrap.querySelector('[data-kbw-bp-file-remove]');
    if (!input) return;

    if (trigger) {
      trigger.addEventListener('click', function () {
        input.click();
      });
    }

    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      hideFileError(wrap);

      if (!file) {
        hideFileName(wrap);
        return;
      }

      if (file.size > MAX_FILE_BYTES) {
        showFileError(wrap, FILE_TOO_LARGE_MESSAGE);
        clearFile(wrap);
        return;
      }

      if (!fileTypeIsAllowed(file)) {
        showFileError(wrap, FILE_WRONG_TYPE_MESSAGE);
        clearFile(wrap);
        return;
      }

      showFileName(wrap, file.name);
    });

    if (removeButton) {
      removeButton.addEventListener('click', function () {
        clearFile(wrap);
      });
    }
  }

  function resetFileFields(form) {
    var wraps = form.querySelectorAll('[data-kbw-bp-file]');
    for (var i = 0; i < wraps.length; i++) {
      clearFile(wraps[i]);
    }
  }

  function resetTurnstile(form) {
    var widget = form.querySelector('[data-kbw-bp-turnstile]');
    if (widget && window.turnstile && typeof window.turnstile.reset === 'function') {
      window.turnstile.reset(widget);
    }
  }

  function showOk(form) {
    var ok = form.querySelector('[data-kbw-bp-ok]');
    var err = form.querySelector('[data-kbw-bp-err]');
    if (err) err.hidden = true;
    if (ok) {
      ok.hidden = false;
      ok.focus();
    }
  }

  function showError(form, message) {
    var ok = form.querySelector('[data-kbw-bp-ok]');
    var err = form.querySelector('[data-kbw-bp-err]');
    var errText = form.querySelector('[data-kbw-bp-err-text]');
    if (ok) ok.hidden = true;
    if (errText) errText.textContent = message;
    if (err) {
      err.hidden = false;
      err.focus();
    }
  }

  function hideMessages(form) {
    var ok = form.querySelector('[data-kbw-bp-ok]');
    var err = form.querySelector('[data-kbw-bp-err]');
    if (ok) ok.hidden = true;
    if (err) err.hidden = true;
  }

  function setUpForm(form) {
    var endpoint = form.getAttribute('data-endpoint');
    var submitButton = form.querySelector('[data-kbw-bp-submit]');
    var submitLabel = submitButton ? (submitButton.getAttribute('data-label') || submitButton.textContent) : '';
    var submitting = false;

    var fileWraps = form.querySelectorAll('[data-kbw-bp-file]');
    for (var i = 0; i < fileWraps.length; i++) {
      setUpFileField(fileWraps[i]);
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitting || !endpoint) return;

      submitting = true;
      hideMessages(form);
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Submitting…';
      }

      var body = new FormData(form);

      fetch(endpoint, { method: 'POST', body: body })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (json) {
              if (json && json.ok) {
                showOk(form);
                form.reset();
                resetFileFields(form);
              } else {
                showError(form, (json && json.error) || GENERIC_ERROR_MESSAGE);
                resetTurnstile(form);
              }
            });
        })
        .catch(function () {
          showError(form, NETWORK_ERROR_MESSAGE);
          resetTurnstile(form);
        })
        .then(function () {
          submitting = false;
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = submitLabel;
          }
        });
    });
  }

  var forms = document.querySelectorAll('[data-kbw-bp-form]');
  for (var i = 0; i < forms.length; i++) {
    setUpForm(forms[i]);
  }
})();
